import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, Clock, CheckCircle, Shield, Zap, Users, ArrowRight, Star, AlertTriangle } from "lucide-react";
import { Header, Footer, StickyCTA, QuoteForm } from "../components/Layout";
import seoData from "../data/seoVilles.json";

// Données services - ton pro, humain, rassurant
const SERVICES = {
  desinsectisation: {
    nom: "Désinsectisation",
    icon: "🐜",
    couleur: "emerald",
    accroche: "Cafards, fourmis, puces ou blattes ? On élimine tous les insectes nuisibles.",
    titre: "Désinsectisation Alpes-Maritimes (06) : Nice, Cannes, Antibes | Azur Tech",
    metaDescription: "Désinsectisation dans les Alpes-Maritimes (06) : Nice, Cannes, Antibes, Grasse. Traitement cafards, blattes, fourmis dès 80€. Intervention 24h/24, résultat garanti ☎ 07 64 07 95 35",
    description: "Les insectes nuisibles se reproduisent vite. Un cafard femelle peut donner naissance à 400 descendants par an. Plus vous agissez tôt, plus c'est simple et rapide à traiter. On intervient le jour même si c'est urgent.",
    faq: [
      { question: "Combien coûte une désinsectisation à Nice ?", answer: "Une désinsectisation à Nice coûte à partir de 80€ pour un traitement ponctuel. Le prix varie selon la surface, le type d'insecte et le niveau d'infestation. Nous proposons un devis gratuit sur place." },
      { question: "Combien de temps dure un traitement contre les cafards ?", answer: "Un traitement contre les cafards nécessite généralement 2 à 3 passages espacés de 15 jours. Le premier passage élimine les adultes, les suivants éliminent les jeunes issus des œufs." },
      { question: "Les produits utilisés sont-ils dangereux ?", answer: "Nous utilisons des produits professionnels homologués, appliqués de manière ciblée. Ils sont sans danger pour les humains et animaux domestiques après séchage (2-4 heures)." },
      { question: "Intervenez-vous en urgence pour les guêpes ?", answer: "Oui, nous intervenons en urgence 24h/24 pour les nids de guêpes et frelons, notamment en cas de danger immédiat (allergie, présence d'enfants)." },
    ],
    insectes: [
      { nom: "Cafards et blattes", desc: "Les plus courants. Ils sortent la nuit et se cachent le jour. Déjections noires, odeur caractéristique.", danger: "Bactéries, allergies" },
      { nom: "Punaises de lit", desc: "Se nourrissent de sang la nuit. Piqûres alignées au réveil, taches sur les draps.", danger: "Démangeaisons, insomnie" },
      { nom: "Fourmis", desc: "Envahissent les cuisines à la recherche de nourriture. Files visibles.", danger: "Contamination alimentaire" },
      { nom: "Guêpes et frelons", desc: "Nids dans les combles, volets ou arbres. Dangereux à retirer seul.", danger: "Piqûres, allergies graves" },
    ],
    methodes: [
      { titre: "Diagnostic précis", desc: "On identifie l'espèce et les zones touchées avant d'agir." },
      { titre: "Traitement ciblé", desc: "Gel, pulvérisation ou nébulisation selon le cas." },
      { titre: "Traitement thermique", desc: "Pour les punaises de lit, efficace à 100%." },
      { titre: "Suivi garanti", desc: "On revient si le problème persiste." },
    ],
    prix: "À partir de 80€",
  },
  deratisation: {
    nom: "Dératisation",
    icon: "🐀",
    couleur: "blue",
    accroche: "Rats ou souris chez vous ? On les élimine et on bloque leurs accès.",
    titre: "Dératisation Alpes-Maritimes (06) : Nice, Cannes, Antibes | Azur Tech",
    metaDescription: "Dératisation dans les Alpes-Maritimes (06) : Nice, Cannes, Antibes, Menton. Élimination rats et souris garantie dès 120€. Urgence 24h/24, devis gratuit ☎ 07 64 07 95 35",
    description: "Un couple de rats peut engendrer 2000 descendants par an. Ils rongent tout : câbles, isolation, nourriture. Et ils transmettent des maladies. Plus vous attendez, plus c'est compliqué. On intervient vite et on vous débarrasse du problème.",
    faq: [
      { question: "Comment savoir si j'ai des rats ou des souris ?", answer: "Les signes révélateurs sont : crottes (2cm pour rats, 3-8mm pour souris), bruits de grattement la nuit, traces de gras le long des murs, odeur d'urine, câbles ou emballages rongés." },
      { question: "Combien coûte une dératisation à Nice ?", answer: "Une dératisation à Nice coûte à partir de 120€ pour un appartement. Le prix dépend de la surface et du niveau d'infestation. Devis gratuit sur place." },
      { question: "Combien de temps pour se débarrasser des rats ?", answer: "Comptez 2 à 4 semaines pour éliminer une infestation de rats. Nous effectuons plusieurs passages pour poser les appâts, contrôler et reboucher les accès." },
      { question: "Les appâts sont-ils dangereux pour mes animaux ?", answer: "Nous utilisons des postes d'appâtage sécurisés, inaccessibles aux enfants et animaux domestiques. Les appâts sont placés dans des boîtiers fermés à clé." },
    ],
    insectes: [
      { nom: "Rat brun (d'égout)", desc: "Le plus courant. Vit au sol, dans les caves et canalisations. Crottes de 2 cm.", danger: "Leptospirose, dégâts" },
      { nom: "Rat noir", desc: "Excellent grimpeur. Vit en hauteur, dans les greniers et faux plafonds.", danger: "Maladies, incendie" },
      { nom: "Souris", desc: "Plus petite, s'infiltre partout. Crottes de 3-8 mm.", danger: "Contamination alimentaire" },
    ],
    methodes: [
      { titre: "Inspection complète", desc: "On repère les accès, les nids et les traces de passage." },
      { titre: "Pose d'appâts", desc: "Postes sécurisés avec rodenticides professionnels." },
      { titre: "Pièges", desc: "Tapettes et pièges multi-captures si nécessaire." },
      { titre: "Obturation des accès", desc: "On bouche les trous pour éviter le retour." },
    ],
    prix: "À partir de 120€",
  },
  desinfection: {
    nom: "Désinfection",
    icon: "🦠",
    couleur: "teal",
    accroche: "Désinfection après nuisibles, virus ou odeurs tenaces.",
    titre: "Désinfection Nice & Alpes-Maritimes (06) | Azur Tech",
    metaDescription: "Désinfection professionnelle Nice et Alpes-Maritimes (06). Traitement après nuisibles, virus, odeurs. Particuliers et professionnels. Devis gratuit ☎ 07 64 07 95 35",
    description: "Après une infestation de rats ou un problème d'hygiène, la désinfection est souvent nécessaire. On élimine les bactéries, virus et mauvaises odeurs pour un environnement sain. Particuliers et professionnels.",
    faq: [
      { question: "Quand faut-il faire une désinfection ?", answer: "Une désinfection est recommandée après une infestation de rongeurs (déjections contaminées), après un sinistre (inondation, décès), ou pour traiter des odeurs persistantes." },
      { question: "Combien de temps dure une désinfection ?", answer: "Une désinfection prend généralement 2 à 4 heures selon la surface. Le local est accessible après 4 à 6 heures de séchage et aération." },
      { question: "La désinfection élimine-t-elle les odeurs ?", answer: "Oui, nous utilisons des techniques spécifiques (ozonation, nébulisation) pour neutraliser les odeurs tenaces, même après un décès ou un syndrome de Diogène." },
    ],
    insectes: [
      { nom: "Après nuisibles", desc: "Les rongeurs laissent des déjections et de l'urine contaminées.", danger: "Leptospirose, hantavirus" },
      { nom: "Après sinistre", desc: "Inondation, décès, syndrome de Diogène.", danger: "Risques sanitaires" },
      { nom: "Traitement odeurs", desc: "Odeurs persistantes malgré le nettoyage.", danger: "Inconfort, dévaluation" },
      { nom: "Désinfection pro", desc: "Restaurants, bureaux, locaux commerciaux.", danger: "Non-conformité" },
    ],
    methodes: [
      { titre: "Évaluation", desc: "On évalue l'état des lieux et les zones à traiter." },
      { titre: "Nettoyage", desc: "Élimination des déchets et matières organiques." },
      { titre: "Désinfection", desc: "Pulvérisation ou nébulisation de désinfectants." },
      { titre: "Traitement de l'air", desc: "Ozonation ou ionisation pour les odeurs." },
    ],
    prix: "Sur devis",
  },
  "punaises-de-lit": {
    nom: "Punaises de lit",
    icon: "🛏️",
    couleur: "purple",
    accroche: "Punaises de lit ? On les élimine avec les meilleures techniques.",
    titre: "Punaises de Lit Nice & Alpes-Maritimes (06) : Traitement Garanti | Azur Tech",
    metaDescription: "Traitement punaises de lit Nice, Cannes et Alpes-Maritimes (06). Méthode thermique et chimique efficace à 100% dès 150€. Intervention rapide, résultat garanti ☎ 07 64 07 95 35",
    description: "Les punaises de lit sont un vrai fléau. Elles se cachent dans votre literie et vous piquent la nuit. Les traitements grand public ne fonctionnent pas. Seul un traitement professionnel (thermique ou chimique) peut en venir à bout.",
    faq: [
      { question: "Comment savoir si j'ai des punaises de lit ?", answer: "Les signes sont : piqûres alignées ou groupées au réveil, petites taches de sang sur les draps, points noirs (déjections) près du matelas, et parfois une odeur douceâtre." },
      { question: "Combien coûte un traitement punaises de lit à Nice ?", answer: "Un traitement punaises de lit coûte à partir de 150€ par pièce pour un traitement chimique, et 400-600€ pour un traitement thermique. Le thermique est plus cher mais efficace en un seul passage." },
      { question: "Faut-il jeter son matelas ?", answer: "Non, dans la plupart des cas le matelas peut être traité et conservé. Jeter le matelas sans traitement risque de contaminer d'autres zones. Nous vous conseillerons sur place." },
      { question: "Le traitement thermique est-il vraiment efficace à 100% ?", answer: "Oui, le traitement thermique à 56°C tue toutes les punaises et leurs œufs en un seul passage. C'est la méthode la plus efficace, recommandée pour les infestations importantes." },
      { question: "Dois-je quitter mon logement pendant le traitement ?", answer: "Pour un traitement chimique, comptez 4-6 heures d'absence. Pour un traitement thermique, la journée entière. Nous vous donnons toutes les consignes de préparation." },
    ],
    insectes: [
      { nom: "Comment les reconnaître", desc: "5-7 mm, brun-rouge, aplaties. Se cachent le jour, piquent la nuit.", danger: "Piqûres, démangeaisons" },
      { nom: "Signes d'infestation", desc: "Piqûres alignées, taches de sang sur les draps, points noirs près du matelas.", danger: "Insomnie, anxiété" },
      { nom: "D'où viennent-elles", desc: "Voyages, meubles d'occasion, voisins, valises.", danger: "Propagation rapide" },
    ],
    methodes: [
      { titre: "Traitement thermique", desc: "On chauffe à 56°C pour tuer punaises et œufs. Efficace à 100%." },
      { titre: "Traitement chimique", desc: "Insecticides pros en 2-3 passages. Moins cher, très efficace." },
      { titre: "Vapeur sèche", desc: "180°C sur les textiles et recoins. Complément idéal." },
      { titre: "Traitement combiné", desc: "Pour les infestations importantes. Garantie maximale." },
    ],
    prix: "À partir de 150€/pièce",
  },
};

export default function ServicePage() {
  const { service } = useParams();
  const path = window.location.pathname.replace("/", "");
  const key = service || path;
  const data = SERVICES[key];

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Service non trouvé</h1>
          <Link to="/" className="text-emerald-600 hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  const colorMap = {
    emerald: { bg: "from-emerald-600 to-emerald-700", light: "bg-emerald-50", text: "text-emerald-600" },
    blue: { bg: "from-blue-600 to-blue-700", light: "bg-blue-50", text: "text-blue-600" },
    teal: { bg: "from-teal-600 to-teal-700", light: "bg-teal-50", text: "text-teal-600" },
    purple: { bg: "from-purple-600 to-purple-700", light: "bg-purple-50", text: "text-purple-600" },
  };
  const colors = colorMap[data.couleur] || colorMap.emerald;

  // Générer le FAQ Schema JSON-LD
  const faqSchema = data.faq ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${data.nom} Alpes-Maritimes (06)`,
    "serviceType": data.nom,
    "description": data.metaDescription,
    "url": `https://azurtech06.fr/${key}`,
    "provider": {
      "@type": "PestControlService",
      "@id": "https://azurtech06.fr/#organization",
      "name": "Azur Tech",
      "telephone": "+33764079535",
      "url": "https://azurtech06.fr"
    },
    "areaServed": [
      { "@type": "AdministrativeArea", "name": "Alpes-Maritimes (06)" },
      { "@type": "City", "name": "Nice" },
      { "@type": "City", "name": "Cannes" },
      { "@type": "City", "name": "Antibes" },
      { "@type": "City", "name": "Grasse" },
      { "@type": "City", "name": "Menton" }
    ]
  };

  const villes06 = seoData.villes.filter((v) => v.dept === "06");
  const villesIDF = seoData.villes.filter((v) => ["75", "77", "78", "91", "92", "93", "94", "95"].includes(v.dept));

  return (
    <div className="min-h-screen bg-white pb-16 md:pb-0">
      <Helmet>
        <title>{data.titre}</title>
        <meta name="description" content={data.metaDescription} />
        <link rel="canonical" href={`https://azurtech06.fr/${key}`} />
        <meta property="og:title" content={data.titre} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:url" content={`https://azurtech06.fr/${key}`} />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">
            {JSON.stringify(faqSchema)}
          </script>
        )}
      </Helmet>
      <Header />

      {/* Hero */}
      <section className={`pt-28 md:pt-36 pb-12 bg-gradient-to-br ${colors.bg} text-white`}>
        <div className="container mx-auto px-4">
          <div className="text-5xl mb-4">{data.icon}</div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            {data.nom} dans les Alpes-Maritimes (06)
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mb-6">
            {data.accroche}
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="tel:0764079535" className="inline-flex items-center space-x-2 bg-white text-[#0f172a] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all">
              <Phone className="w-5 h-5" />
              <span>07 64 07 95 35</span>
            </a>
            <a href="#devis" className="inline-flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-xl font-semibold transition-all border border-white/30">
              <span>Devis gratuit</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-6 bg-gray-50 border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-6 md:gap-12 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-emerald-500" />
              <span><strong>24h/24</strong> même le week-end</span>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-emerald-500" />
              <span><strong>Garantie</strong> résultat</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="w-5 h-5 text-emerald-500" />
              <span><strong>4.9/5</strong> avis clients</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main */}
            <div className="lg:col-span-2 space-y-8">
              {/* Intro */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-4">
                  Pourquoi agir vite ?
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {data.description}
                </p>
              </div>

              {/* Types */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-6">
                  Ce qu'on traite
                </h2>
                <div className="space-y-4">
                  {data.insectes.map((item, i) => (
                    <div key={i} className={`${colors.light} rounded-xl p-4`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold text-[#0f172a] mb-1">{item.nom}</h3>
                          <p className="text-gray-600 text-sm mb-2">{item.desc}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <AlertTriangle className="w-4 h-4 text-orange-500" />
                        <span className="text-orange-700">Risques : {item.danger}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Méthodes */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-6">
                  Comment on procède
                </h2>
                <div className="space-y-4">
                  {data.methodes.map((m, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className={`w-8 h-8 ${colors.light} ${colors.text} rounded-full flex items-center justify-center font-bold flex-shrink-0`}>
                        {i + 1}
                      </div>
                      <div>
                        <h3 className="font-bold text-[#0f172a]">{m.titre}</h3>
                        <p className="text-gray-600 text-sm">{m.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Avantages */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-[#0f172a] mb-6">
                  Pourquoi nous choisir ?
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: <Zap className="w-5 h-5" />, titre: "Intervention rapide", desc: "Sous 2h en cas d'urgence" },
                    { icon: <Shield className="w-5 h-5" />, titre: "Résultat garanti", desc: "On revient gratuitement si besoin" },
                    { icon: <Users className="w-5 h-5" />, titre: "Techniciens formés", desc: "Certifiés et bien équipés" },
                    { icon: <CheckCircle className="w-5 h-5" />, titre: "Prix transparent", desc: "Devis détaillé, pas de surprise" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-xl">
                      <div className="text-emerald-500">{item.icon}</div>
                      <div>
                        <p className="font-semibold text-[#0f172a] text-sm">{item.titre}</p>
                        <p className="text-gray-500 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Prix */}
              <div className={`${colors.light} rounded-2xl p-6 md:p-8`}>
                <h2 className="text-2xl font-bold text-[#0f172a] mb-2">
                  Tarifs {data.nom.toLowerCase()}
                </h2>
                <p className={`text-xl font-bold ${colors.text} mb-3`}>{data.prix}</p>
                <p className="text-gray-600 text-sm">
                  Le tarif exact dépend de la surface et du niveau d'infestation. On vous fait un devis gratuit avant toute intervention.
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28" id="devis">
                <QuoteForm service={data.nom} />
                
                {/* Urgence */}
                <div className="mt-6 bg-[#0f172a] rounded-2xl p-6 text-white text-center">
                  <p className="text-sm text-gray-400 mb-2">Besoin d'une {data.nom.toLowerCase()} ?</p>
                  <a href="tel:0764079535" className="text-2xl font-bold hover:text-emerald-400 transition-colors">
                    07 64 07 95 35
                  </a>
                  <p className="text-emerald-400 text-sm mt-1">On répond 24h/24</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-12 bg-gradient-to-r ${colors.bg}`}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Besoin d'une {data.nom.toLowerCase()} ?
          </h2>
          <p className="text-white/80 mb-6">On vous répond en moins de 15 minutes.</p>
          <a href="tel:0764079535" className="inline-flex items-center space-x-2 bg-white text-[#0f172a] px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all">
            <Phone className="w-5 h-5" />
            <span>07 64 07 95 35</span>
          </a>
        </div>
      </section>

      {/* FAQ Section */}
      {data.faq && data.faq.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-8">
              Questions fréquentes sur la {data.nom.toLowerCase()}
            </h2>
            <div className="max-w-3xl space-y-4">
              {data.faq.map((item, index) => (
                <details key={index} className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden group">
                  <summary className="px-6 py-4 cursor-pointer font-medium text-gray-800 hover:bg-gray-100 transition-colors flex justify-between items-center">
                    {item.question}
                    <span className="text-emerald-500 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="px-6 py-4 text-gray-600 border-t border-gray-200 bg-white">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Maillage interne - Zones d'intervention */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-800 mb-6">
            {data.nom} ville par ville dans les Alpes-Maritimes (06)
          </h2>
          <div className="flex flex-wrap gap-2 mb-8">
            {villes06.map((ville) => (
              <Link key={ville.slug} to={`/${key}-${ville.slug}`} className="px-3 py-1 bg-white border border-gray-200 text-gray-600 rounded-full text-sm hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition-colors">
                {data.nom} {ville.nom}
              </Link>
            ))}
          </div>

          <h2 className="text-xl font-bold text-gray-800 mb-6">
            {data.nom} en Île-de-France
          </h2>
          <div className="flex flex-wrap gap-2 mb-8">
            {villesIDF.map((ville) => (
              <Link key={ville.slug} to={`/${key}-${ville.slug}`} className="px-3 py-1 bg-white border border-gray-200 text-gray-600 rounded-full text-sm hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition-colors">
                {data.nom} {ville.nom}
              </Link>
            ))}
          </div>
          
          {/* Autres services */}
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Nos autres services</h3>
          <div className="flex flex-wrap gap-3">
            {key !== "desinsectisation" && (
              <Link to="/desinsectisation" className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors font-medium">
                Désinsectisation
              </Link>
            )}
            {key !== "deratisation" && (
              <Link to="/deratisation" className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-medium">
                Dératisation
              </Link>
            )}
            {key !== "punaises-de-lit" && (
              <Link to="/punaises-de-lit" className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors font-medium">
                Punaises de lit
              </Link>
            )}
            {key !== "desinfection" && (
              <Link to="/desinfection" className="px-4 py-2 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors font-medium">
                Désinfection
              </Link>
            )}
          </div>
        </div>
      </section>

      <Footer />
      <StickyCTA />
    </div>
  );
}
